import { Component, lazy, Suspense, useEffect, useId, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Gamepad2, LoaderCircle, X } from 'lucide-react';
import '../pages/Games.css';

const DispatchDash = lazy(() => import('../games/dispatch-dash/DispatchDash'));
const ClearTheLane = lazy(() => import('../games/clear-the-lane/ClearTheLane'));
const HookAndHaul = lazy(() => import('../games/hook-and-haul/HookAndHaul'));

const GAME_DETAILS = {
  'dispatch-dash': {
    title: 'Dispatch Dash',
    loadingTitle: 'Getting your truck ready…',
    loadingCopy: 'Dispatch will be with you in a moment.',
    component: DispatchDash,
  },
  'clear-the-lane': {
    title: 'Clear the Lane',
    loadingTitle: 'Setting up the lane…',
    loadingCopy: 'Your first roadside puzzle will be ready in a moment.',
    component: ClearTheLane,
  },
  'hook-and-haul': {
    title: 'J20: Hook & Haul',
    loadingTitle: 'Rigging J20 for recovery…',
    loadingCopy: 'The winch line, terrain and recovery board are coming up.',
    component: HookAndHaul,
  },
};

class GameLoadBoundary extends Component {
  state = { failed: false };

  static getDerivedStateFromError() {
    return { failed: true };
  }

  render() {
    if (this.state.failed) {
      return (
        <div className="dispatch-game-loading" role="alert">
          <Gamepad2 size={34} />
          <strong>The game couldn’t load this time.</strong>
          <p>Your page is still available. Refresh it to try loading the game again.</p>
          <button type="button" onClick={this.props.onClose}>Back to the page</button>
        </div>
      );
    }
    return this.props.children;
  }
}

/** The parent keeps its status polling active; only a source label reaches the game. */
export default function DispatchGamePanel({ game = 'dispatch-dash', source, onClose, statusLabel, statusStale = false }) {
  const backdropRef = useRef(null);
  const panelRef = useRef(null);
  const closeRef = useRef(null);
  const onCloseRef = useRef(onClose);
  const [mounted, setMounted] = useState(false);
  const titleId = useId();
  const details = GAME_DETAILS[game] || GAME_DETAILS['dispatch-dash'];
  const GameComponent = details.component;
  onCloseRef.current = onClose;

  useEffect(() => {
    setMounted(true);
    const previousFocus = document.activeElement;
    const previousOverflow = document.body.style.overflow;
    const appRoot = document.getElementById('root');
    const rootWasInert = appRoot?.hasAttribute('inert');
    document.body.style.overflow = 'hidden';
    appRoot?.setAttribute('inert', '');

    function handleKeys(event) {
      const panel = panelRef.current;
      if (!panel || event.defaultPrevented || panel.querySelector('dialog[open]')) return;
      if (event.target instanceof Element && event.target.closest('dialog[open]')) return;
      if (event.key === 'Escape') {
        event.preventDefault();
        onCloseRef.current();
      } else if (event.key === 'Tab') {
        const candidates = [...panel.querySelectorAll('button:not(:disabled),a[href],input:not(:disabled),select:not(:disabled),textarea:not(:disabled),[tabindex]:not([tabindex="-1"])')]
          .filter((element) => element.getClientRects().length > 0);
        const first = candidates[0];
        const last = candidates[candidates.length - 1];
        if (!first) {
          event.preventDefault();
          panel.focus();
        } else if (event.shiftKey && (document.activeElement === first || !panel.contains(document.activeElement))) {
          event.preventDefault();
          last.focus();
        } else if (!event.shiftKey && (document.activeElement === last || !panel.contains(document.activeElement))) {
          event.preventDefault();
          first.focus();
        }
      }
    }

    document.addEventListener('keydown', handleKeys);
    return () => {
      document.removeEventListener('keydown', handleKeys);
      document.body.style.overflow = previousOverflow;
      if (!rootWasInert) appRoot?.removeAttribute('inert');
      if (previousFocus instanceof HTMLElement && previousFocus.isConnected) previousFocus.focus();
    };
  }, []);

  useEffect(() => {
    if (mounted) closeRef.current?.focus();
  }, [mounted]);

  useEffect(() => {
    if (!mounted) return undefined;
    const viewport = window.visualViewport;
    const measureViewport = () => {
      const height = Math.round(viewport?.height || window.innerHeight);
      if (height > 0) backdropRef.current?.style.setProperty('--game-viewport-height', `${height}px`);
    };
    measureViewport();
    viewport?.addEventListener('resize', measureViewport);
    viewport?.addEventListener('scroll', measureViewport);
    window.addEventListener('resize', measureViewport);
    window.addEventListener('orientationchange', measureViewport);
    return () => {
      viewport?.removeEventListener('resize', measureViewport);
      viewport?.removeEventListener('scroll', measureViewport);
      window.removeEventListener('resize', measureViewport);
      window.removeEventListener('orientationchange', measureViewport);
    };
  }, [mounted]);

  if (!mounted) return null;

  return createPortal(
    <div className="dispatch-game-backdrop" ref={backdropRef}>
      <section className={`dispatch-game-panel ${game === 'clear-the-lane' ? 'dispatch-game-panel--lane' : ''} ${game === 'hook-and-haul' ? 'dispatch-game-panel--hook' : ''}`} ref={panelRef} role="dialog" aria-modal="true" aria-labelledby={titleId} tabIndex={-1}>
        <header className="dispatch-game-panel__bar">
          <div className="dispatch-game-panel__title"><Gamepad2 size={18} /><strong id={titleId}>{details.title}</strong></div>
          {statusLabel && (
            <div className={`dispatch-game-live-status ${statusStale ? 'is-stale' : ''}`} role="status" aria-live="polite">
              <span aria-hidden="true" />
              <strong>{statusStale ? 'Reconnecting to service updates' : statusLabel}</strong>
              {!statusStale && <small>Your real service · live</small>}
            </div>
          )}
          <button className="dispatch-game-panel__close" type="button" ref={closeRef} onClick={onClose} aria-label={source === 'service_status' ? 'Close game and return to your service status' : 'Close game'}><X size={21} /></button>
        </header>
        <div className="dispatch-game-panel__body">
          <GameLoadBoundary onClose={onClose}>
            <Suspense fallback={<div className="dispatch-game-loading" role="status"><LoaderCircle className="dispatch-game-loading__spinner" size={32} /><strong>{details.loadingTitle}</strong><p>{details.loadingCopy}</p></div>}>
              <GameComponent source={source} onClose={onClose} />
            </Suspense>
          </GameLoadBoundary>
        </div>
      </section>
    </div>,
    document.body,
  );
}
